const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

export const UNITS = {
  DAYS: 24 * 60 * 60 * 1000,
  HOURS: 60 * 60 * 1000,
  MINUTES: 60 * 1000,
  SECONDS: 1000
};

// formats a date object like 'YYYY-MM-DD'
export function formatDate(dateObj) {
  const year = dateObj.getFullYear();
  const month = leftPad(dateObj.getMonth() + 1, 2);
  const day = leftPad(dateObj.getDate(), 2);
  return year + '-' + month + '-' + day;
}

// formats a time object like 'HH:mm'
export function formatTime(dateObj) {
  const hours = leftPad(dateObj.getHours(), 2);
  const minutes = leftPad(dateObj.getMinutes(), 2);
  return hours + ':' + minutes;
}

export function calcTimezoneOffsetDiff(timezoneOffset1, timezoneOffset2) {
  const diff = Math.abs(timezoneOffset1 - timezoneOffset2);
  return diff - timezoneOffset1;
}

export function leftPad(n, len) {
  return new Array(len - String(n).length + 1).join('0').concat(n);
}

// parse a date with the format 'YYYY-MM-DD'
// and return an array like
// [year, month, day]
function parseDate(dateStr) {
  return dateStr.split('-').map(piece => parseInt(piece, 10));
}

// parse a time with the format 'HH:MM'
// and return an array like
// [hour, minutes]
function parseTime(timeStr) {
  return timeStr.split(':').map(piece => parseInt(piece, 10));
}

// This code was taken from https://github.com/moment/moment-timezone/blob/develop/moment-timezone.js#L164-L188
function parseZone(timestamp, zoneInfo, subtractOffset = true) {
  let target = +timestamp,
    offsets = zoneInfo.offsets,
    untils = zoneInfo.untils,
    max = untils.length - 1,
    offset,
    offsetPrev,
    i;

  for (i = 0; i < max; i++) {
    offset = offsets[i];
    offsetPrev = offsets[i ? i - 1 : i];

    if (offset > offsetPrev) {
      offset = offsetPrev;
    }

    const delta = subtractOffset ? offset * 60000 : 0;

    if (target < untils[i] - delta) {
      return offsets[i];
    }
  }

  return offsets[max];
}

export default class TZDate {
  constructor(timestamp = Date.now(), zoneInfo) {
    this._timestamp = timestamp;
    this._zoneInfo = zoneInfo;
  }

  static createFromDateAndTimeString(dateStr, timeStr = '00:00', zoneInfo) {
    const [year, month, day] = parseDate(dateStr);
    const [hour, minute] = parseTime(timeStr);
    let timestamp;
    let timezoneOffset;

    if (!zoneInfo) {
      const date = new Date(year, month - 1, day, hour, minute, 0);
      timestamp = date.getTime();
      timezoneOffset = date.getTimezoneOffset();
    } else {
      const tempTimestamp = Date.UTC(year, month - 1, day, hour, minute, 0);
      timezoneOffset = parseZone(tempTimestamp, zoneInfo);
      timestamp = tempTimestamp + timezoneOffset * 60000;
    }

    return new TZDate(timestamp, zoneInfo);
  }

  getTimezoneOffset() {
    if (this._zoneInfo) {
      return parseZone(this._timestamp, this._zoneInfo, false);
    } else {
      return new Date(this._timestamp).getTimezoneOffset();
    }
  }

  getDateWithOffset() {
    const date = new Date(this._timestamp);
    const timestamp = this._timestamp +
      date.getTimezoneOffset() * 60000 -
      this.getTimezoneOffset() * 60000;
    return new Date(timestamp);
  }

  // returns a date string like 'YYYY-MM-DD'
  getLocalDateString() {
    return formatDate(this.getDateWithOffset());
  }

  // returns a time string like 'HH:mm'
  getLocalTimeString() {
    return formatTime(this.getDateWithOffset());
  }

  getLocalDateTimeString() {
    return `${this.getLocalDateString()} ${this.getLocalTimeString()}`;
  }

  getFullYear() {
    return this.getDateWithOffset().getFullYear();
  }

  getMonth() {
    return this.getDateWithOffset().getMonth();
  }

  getDate() {
    return this.getDateWithOffset().getDate();
  }

  getDay() {
    return this.getDateWithOffset().getDay();
  }

  getHours() {
    return this.getDateWithOffset().getHours();
  }

  getMinutes() {
    return this.getDateWithOffset().getMinutes();
  }

  getDayString() {
    return DAYS[this.getDay()];
  }

  getTimestamp() {
    return this._timestamp;
  }

  toString() {
    return this.getLocalDateTimeString();
  }

  add(val, unitType) {
    return new TZDate(
      this._timestamp + val * UNITS[unitType.toUpperCase()],
      this._zoneInfo
    );
  }

  // returns a new TZDate object which has a certain number of days added to it
  addDays(days) {
    return this.add(days, 'days');
  }

  // check if two tzDates are equal
  equals(otherTZDate) {
    return this.getTimestamp() === otherTZDate.getTimestamp();
  }

  isSameDay(otherTZDate) {
    return this.getLocalDateString() === otherTZDate.getLocalDateString();
  }

  isBefore(otherTZDate) {
    return this.getTimestamp() < otherTZDate.getTimestamp();
  }

  isSameOrBefore(otherTZDate) {
    return this.getTimestamp() <= otherTZDate.getTimestamp();
  }

  isAfter(otherTZDate) {
    return this.getTimestamp() > otherTZDate.getTimestamp();
  }

  isSameOrAfter(otherTZDate) {
    return this.getTimestamp() >= otherTZDate.getTimestamp();
  }

  isBetween(startTZDate, endTZDate) {
    return this._timestamp > startTZDate.getTimestamp() &&
      this._timestamp < endTZDate.getTimestamp();
  }

  diff(otherTZDate) {
    return this._timestamp - otherTZDate.getTimestamp();
  }

  setTime(timeStr) {
    return TZDate.createFromDateAndTimeString(
      this.getLocalDateString(),
      timeStr,
      this._zoneInfo
    );
  }

  // convert the current date to another zone
  toZone(zoneInfo) {
    return new TZDate(this._timestamp, zoneInfo);
  }

  clone() {
    return new TZDate(this._timestamp, this._zoneInfo);
  }
}



// WEBPACK FOOTER //
// ./src/commons/services/TZDate.js